import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { DeliveryDashboardSettings } from "app/types/deliveryDashboardSettings";
import { RoutePicking, RouteStop } from "app/types/route";
import { getRelationId } from "app/utils/odooDisplay";
import { releasePickingForReassignment } from "app/utils/odooApi";
import { handleApiError } from "app/utils/apiErrorHandler";
import {
  canReleaseStopForReassignment,
  isManualRouteReleaseMode,
} from "app/utils/routeReassignment";

type UseRouteStopsListOptions = {
  stops: RouteStop[];
  accessToken: string;
  embedded?: boolean;
  dashboardSettings: DeliveryDashboardSettings | null;
  onReleased: () => void | Promise<void>;
};

export function useRouteStopsList({
  stops,
  accessToken,
  embedded = false,
  dashboardSettings,
  onReleased,
}: UseRouteStopsListOptions) {
  const [expandedStopIds, setExpandedStopIds] = useState<number[]>([]);
  const [releasingStopId, setReleasingStopId] = useState<number | null>(null);
  const [isReleasingAll, setIsReleasingAll] = useState(false);

  const manualReleaseMode = isManualRouteReleaseMode(dashboardSettings);

  const releasableStops = useMemo(
    () =>
      stops.filter((stop) => canReleaseStopForReassignment(stop, manualReleaseMode)),
    [manualReleaseMode, stops]
  );

  const allExpanded =
    stops.length > 0 && stops.every((stop) => expandedStopIds.includes(stop.id));

  const showReleaseActions = manualReleaseMode && releasableStops.length > 0;

  useEffect(() => {
    setExpandedStopIds((current) =>
      current.filter((id) => stops.some((stop) => stop.id === id))
    );
  }, [stops]);

  const toggleStopExpanded = (stopId: number) => {
    setExpandedStopIds((current) =>
      current.includes(stopId)
        ? current.filter((id) => id !== stopId)
        : [...current, stopId]
    );
  };

  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpandedStopIds([]);
      return;
    }
    setExpandedStopIds(stops.map((stop) => stop.id));
  };

  const releaseStop = async (stop: RouteStop) => {
    if (!accessToken || releasingStopId != null || isReleasingAll) {
      return;
    }

    const pickingId = getRelationId(stop.picking_id);
    if (pickingId == null) {
      return;
    }

    setReleasingStopId(stop.id);
    try {
      const response = await releasePickingForReassignment(pickingId, accessToken);
      toast.success(response.message || "Delivery released for reassignment.");
      await onReleased();
    } catch (error) {
      await handleApiError(error, {
        embedded,
        fallbackMessage: "Could not release delivery for reassignment.",
      });
    } finally {
      setReleasingStopId(null);
    }
  };

  const releaseAllFailed = async () => {
    if (
      !accessToken ||
      releasableStops.length === 0 ||
      releasingStopId != null ||
      isReleasingAll
    ) {
      return;
    }

    setIsReleasingAll(true);
    let releasedCount = 0;

    try {
      for (const stop of releasableStops) {
        const pickingId = getRelationId(stop.picking_id);
        if (pickingId == null) {
          continue;
        }
        await releasePickingForReassignment(pickingId, accessToken);
        releasedCount += 1;
      }

      if (releasedCount > 0) {
        toast.success(
          releasedCount === 1
            ? "Delivery released for reassignment."
            : `${releasedCount} failed deliveries released for reassignment.`
        );
        await onReleased();
      }
    } catch (error) {
      await handleApiError(error, {
        embedded,
        fallbackMessage: "Could not release failed deliveries.",
      });
      if (releasedCount > 0) {
        await onReleased();
      }
    } finally {
      setIsReleasingAll(false);
    }
  };

  return {
    manualReleaseMode,
    expandedStopIds,
    releasingStopId,
    isReleasingAll,
    allExpanded,
    releasableStops,
    showReleaseActions,
    toggleStopExpanded,
    toggleExpandAll,
    releaseStop,
    releaseAllFailed,
  };
}

export function buildPickingLookup(
  pickings: RoutePicking[] | undefined
): Map<number, RoutePicking> {
  return new Map((pickings || []).map((picking) => [picking.id, picking]));
}
